import { DatePipe, LowerCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, concatMap, EMPTY, Subject } from 'rxjs';
import { ShareReferenceType, Uuid } from '../../../../graphql';
import { VisibilityObserverModule } from '../../../core/directives/visibility-observer.directive';
import { AsideModalSize, HelperService } from '../../../core/services/helper.service';
import { LaunchDarklyService } from '../../../core/services/launchdarkly.service';
import { LocalizePath } from '../../../utils/types/localize-path';
import { DigitalSignatureViewerComponent } from '../digital-signature-viewer/digital-signature-viewer.component';
import { DigitalSignatureApiService } from '../domain/api/digital-signature-list.api.service';
import { GetQuoteDigitalSignaturesResult } from '../domain/api/query/GetDigitalSignatures.generated';

type DigitalSignature = NonNullable<NonNullable<NonNullable<GetQuoteDigitalSignaturesResult>['shares']>['items']>[number];
export type ShareTypeReference = NonNullable<DigitalSignature['shareTypeReference']>['__typename'];
@Component({
    selector: 'app-digital-signature-list',
    imports: [VisibilityObserverModule, DatePipe, LowerCasePipe, TranslateModule],
    templateUrl: './digital-signature-list.component.html',
    styleUrls: [
        '../../../../../src/app/shared/composable-styles/button.scss',
        '../../../../../src/app/shared/composable-styles/text.scss',
        '../../../../../src/app/shared/composable-styles/table.scss',
        './digital-signature-list.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DigitalSignatureListComponent {
    #api = inject(DigitalSignatureApiService);
    #helper = inject(HelperService);
    #lds = inject(LaunchDarklyService);
    digitalSignaturesEnabled = this.#lds.get('digital-signatures');
    #translate = inject(TranslateService);
    dateTimeFormat: string = this.#translate.instant('general.dateTimeFormat' satisfies LocalizePath);
    referenceId = input.required<Uuid>();
    shareReferenceType = input.required<ShareReferenceType>();
    onViewReference = output<{ id: Uuid; type: ShareTypeReference }>();
    loadingElement = viewChild<ElementRef<HTMLDivElement>>('loading');
    digitalSignatures = signal<DigitalSignature[] | null>(null);
    loadNext$ = new Subject<void>();
    hasError = signal(false);
    hasNextPage = signal(true);

    localizedKind = computed(() =>
        this.shareReferenceType() === 'ESTIMATE'
            ? 'quote'
            : (this.#translate.instant('general.variation' satisfies LocalizePath) as string).toLowerCase()
    );

    #loadNextSubscription = this.loadNext$
        .pipe(
            concatMap(() =>
                this.#api
                    .getQuoteDigitalSignatures({
                        shareReferenceType: this.shareReferenceType(),
                        referenceId: this.referenceId(),
                        skip: this.digitalSignatures()?.length,
                        take: 10,
                    })
                    .pipe(catchError(() => this.hasError.set(true)! && EMPTY))
            ),
            takeUntilDestroyed()
        )
        .subscribe(({ data, errors }) => {
            const existing = this.digitalSignatures() ?? [];
            const newBatch = data?.shares?.items;
            if (!newBatch) return this.hasError.set(true);
            this.digitalSignatures.set([...existing, ...newBatch]);
            this.hasNextPage.set(data.shares?.pageInfo.hasNextPage ?? true);
            this.#loadNextIfSpaceAvailable();
        });

    #loadNextIfSpaceAvailable() {
        const { nativeElement } = this.loadingElement() ?? {};
        if (!nativeElement) return;

        const loadingObserver = new IntersectionObserver(
            ([entry], observer) => {
                if (entry?.isIntersecting) this.loadNext$.next();
                observer.disconnect();
            },
            { threshold: 0 }
        );

        loadingObserver.observe(nativeElement);
    }

    showSignature(shareId: Uuid, shareReferenceType: ShareReferenceType) {
        this.#helper.showModalAside(
            DigitalSignatureViewerComponent,
            { initialState: { initialShareId: shareId, shareReferenceType } },
            AsideModalSize.wide
        );
    }
}
